import { useEffect } from "react";
import { useBeforeunload } from "react-beforeunload";
import { useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { AppDispatch } from "../App";
import LeaveSession from "../components/LeaveSession";
import LeaveSessionFinished from "../components/LeaveSessionFinished";
import Preferences from "../components/Preferences";
import QuitSimulation from "../components/QuitSimulation";
import Snackbar from "../components/Snackbar";
import MainMap from "../MainMap/MainMap";
import ExportMenu from "../MainScreen/ExportMenu";
import {
  getActiveModal,
  getUserMenuOpen,
  inactivateModal,
  setUserMenuOpen,
} from "../state/mainGui";
import { getUserIsSpectator } from "../state/session";
import {
  getSimulation,
  getSimulationStatus,
  pauseSimulation,
  rePauseSimulation,
} from "../state/simulation";

import { FixedSidebar } from "./FixedSidebar";
import Header from "./Header";
import LeftCollapsableSidebar from "./LeftCollapsableSidebar";
import styles from "./MainScreen.module.css";
import RightCollapsableSidebar from "./RightCollapsableSidebar";

const MainScreen: React.FC<Props> = (props) => {
  const intl = useIntl();

  // in order to close the userMenu if the user clicks out of the usermenu
  const userMenuOpen = useSelector(getUserMenuOpen);
  const activeModal = useSelector(getActiveModal);
  const userIsSpectator = useSelector(getUserIsSpectator);
  const simulationStatus = useSelector(getSimulationStatus);

  const areYouSureMessage = intl.formatMessage({
    id: "mainscreen.are_you_sure_tab_quit",
    defaultMessage:
      "Closing this tab deletes simulation after 5 minutes. You can return to shis simulation within 5 minutes by choosing 'follow simulation' in the start screen",
  });

  // ask for confirmation when leaving if user is running the simulation
  useBeforeunload((event) => {
    if (!userIsSpectator) {
      // areYouSureMessage is in most browsers actually not shown: they show a default message instead
      return areYouSureMessage;
    }
    event.preventDefault();
    return true;
  });

  const simulation = useSelector(getSimulation);

  useEffect(() => {
    window.onunload = () => {
      // as part of https://github.com/nens/threedi-live-site/issues/300 we pause the simulation and set timeout to 5 minutes
      if (simulation && !userIsSpectator) {
        // pausing the simulation on leave is somehow already happening?!
        if (
          simulationStatus.status === "running" // how come I cannot use? api.SimulationStatusNameEnum.Running
        ) {
          props.pauseSimulation(5 * 60);
        } else if (simulationStatus.status !== "running") {
          console.log(
            "quitting when paused can currently not update the pause timeout to 5 minutes, because below function call rePauseSimulation does not reliably work on browser quit"
          );
          // below line "rePauseSimulation" does actually not work on browser quit because it relies on 2 api calls (and the second one can only be fired when the first one returns).
          // The result is that the second api call is often not executed, resulting in that the simulation actually becomes running (instead of paused).
          // I (Tom) therefore decide that it  is better to leave the simulation paused with the default timeout (instead of wrongly making it running).
          // Goal is to set the timeout to 5 minutes,  but this can thus not reliably be done if simulation is already paused.
          // We thus leave the simulation paused with the default timeout of 30 minutes as defined in PAUSE_TIMEOUT_SECONDS.
          // https://github.com/nens/threedi-live-site/issues/300

          // props.rePauseSimulation(5*60); // deliberately commented out
        }
      }
    };
    return () => {
      window.onunload = null;
    };
  });

  return (
    <div
      className={styles.Layout}
      onClick={() => {
        // This if statement also stops the immedeate reclose of the user menu after the user menu open button was clicked , since
        //  at that moment the store was not updated yet, thus userMenuOpen is still false (since store update happens asynchronous)
        if (userMenuOpen) {
          props.setUserMenuOpen(false);
        }
      }}
    >
      {activeModal !== "NONE" && activeModal !== null ? (
        <>
          {activeModal === "QUIT_SIMULATION" ? <QuitSimulation /> : null}
          {activeModal === "RESTART_SIMULATION" ? <QuitSimulation restart /> : null}
          {activeModal === "SAVE_RESULTS" ? <ExportMenu /> : null}
          {activeModal === "LEAVE_SESSION" ? <LeaveSession /> : null}
          {activeModal === "SIMULATION_FINISHED" ? <LeaveSessionFinished /> : null}
          {activeModal === "PREFERENCES" ? <Preferences /> : null}
        </>
      ) : null}
      <Header />
      <div className={styles.Body}>
        <FixedSidebar />
        <div className={styles.Map}>
          <Snackbar />
          <LeftCollapsableSidebar />
          <MainMap />
          <RightCollapsableSidebar />
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setUserMenuOpen: (userMenuOpen: boolean) => dispatch(setUserMenuOpen(userMenuOpen)),
    inactivateModal: () => dispatch(inactivateModal()),
    pauseSimulation: (seconds: number) => dispatch(pauseSimulation(seconds)),
    rePauseSimulation: (seconds: number) => dispatch(rePauseSimulation(seconds)),
  };
};
type Props = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(MainScreen);
